import { useCallback } from 'react';
import cookies from 'browser-cookies';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { z } from 'zod';
import { G } from '@mobily/ts-belt';
import { getWindow } from '@stitch-fix/mode-react';
import { useGetClientStyleProfileLazyQuery } from '../graphql/getClientStyleProfile';
import {
  DynamicProductAdCreateMutationVariables,
  useDynamicProductAdCreateMutation,
} from '../graphql/DynamicProductAdCreateMutation';
import { browserLogger } from '../../../utils/loggers/browser';
import { DYNAMIC_ADS_COOKIE_NAME } from '../../../utils/helpers';
import { dynamicAdsCookieSchema } from './dynamicAdsCookieSchema';
import { getVisitorIdCookie } from '../../../hooks/useAllocation/useAllocation';
import type { DynamicProductAd } from '../../../types/definitions/vendor/graphql-schema.d';
import { CapitalizedBusinessLine } from '../../../components/SocialButtons';

export const eraseDynamicAdsCookie = () => {
  cookies.erase(DYNAMIC_ADS_COOKIE_NAME, {
    domain: '.stitchfix.com',
    path: '/',
    secure: true,
  });
};

export const useDynamicProductAdCreate = () => {
  const [mutate] = useDynamicProductAdCreateMutation();
  const [getClientStyleProfile] = useGetClientStyleProfileLazyQuery();

  const dynamicProductAdCreate = useCallback(
    async ({
      clientType,
      variables,
    }: {
      clientType: 'signup' | 'login';
      variables: DynamicProductAdCreateMutationVariables;
    }) => {
      let abortReason;

      try {
        const { data, error } = await getClientStyleProfile({
          fetchPolicy: 'no-cache',
        });
        const businessLine = data?.client?.businessLine;
        const hasCompletedStyleProfileQuiz =
          data?.client?.capabilities?.hasCompletedStyleProfile;
        // Enable existing clients to update their DPA if they had one previously
        const isExistingClientIneligible =
          clientType === 'login' &&
          G.isNullable(data?.client?.dynamicProductAd);

        if (businessLine !== 'WOMENS') abortReason = 'business_line';
        if (hasCompletedStyleProfileQuiz)
          abortReason = 'style_profile_complete';
        if (isExistingClientIneligible)
          abortReason = 'existing_client_without_dpa';
        if (error) abortReason = 'get_client_style_profile_error';

        if (abortReason) {
          recordMark('dynamic_product_ad_create_mutation_aborted', {
            reason: abortReason,
            client_type: clientType,
          });

          if (error) throw error;

          return;
        }

        const mutationResponse = await mutate({
          variables,
        });
        const result = mutationResponse.data?.dynamicProductAdCreate?.result;
        const isSuccessful =
          // eslint-disable-next-line no-underscore-dangle
          result?.__typename === 'DynamicProductAdCreateSuccess';

        recordMark('dynamic_product_ad_create_mutation_completed', {
          graph_mutation_succeeded: isSuccessful,
          client_type: clientType,
        });

        if (!isSuccessful) {
          const message =
            // eslint-disable-next-line no-underscore-dangle
            result?.__typename === 'DynamicProductAdCreateError'
              ? result.message
              : 'Unsuccessful mutation result';

          throw new Error(message || 'Unknown error');
        }
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Unknown error';

        // Don't record a mark if the mutation was aborted
        if (!abortReason) {
          recordMark('dynamic_product_ad_create_mutation_completed', {
            graph_mutation_succeeded: false,
            client_type: clientType,
          });
        }

        browserLogger({
          feature: 'dynamic-ads',
          level: 'error',
          message: `Error creating client dynamic product ad. Reason: ${message}`,
          team: 'client-onboarding',
          context: {
            error: e,
            dynamicProductAdId: variables.input.id,
            grouping: variables.input.grouping,
            clientType,
          },
        });
      }
    },
    [mutate, getClientStyleProfile],
  );

  return dynamicProductAdCreate;
};

export const getDynamicAdsCookie = () => {
  const win = getWindow();

  if (!win) return undefined;

  const dpaCookie = cookies.get(DYNAMIC_ADS_COOKIE_NAME);

  if (dpaCookie) {
    try {
      const decodedCookie = new URLSearchParams(dpaCookie);
      const parsedCookie = dynamicAdsCookieSchema.parse({
        id: decodedCookie.get('id'),
        grouping: decodedCookie.get('grouping'),
        visitor_id: decodedCookie.get('visitor_id'),
      });

      const currentVisitorId = getVisitorIdCookie();

      if (parsedCookie.visitor_id !== currentVisitorId) {
        browserLogger({
          feature: 'dynamic-ads',
          level: 'warn',
          message: `Invalid ${DYNAMIC_ADS_COOKIE_NAME} cookie: visitor ID mismatch. Expected ${currentVisitorId}, found ${parsedCookie.visitor_id}`,
          team: 'client-onboarding',
          context: {
            cookieData: parsedCookie,
            visitorId: currentVisitorId,
            referrer: win.document.referrer || '',
          },
        });

        return undefined;
      }

      return parsedCookie;
    } catch (error: unknown) {
      browserLogger({
        feature: 'dynamic-ads',
        level: 'warn',
        message: `Failed to parse ${DYNAMIC_ADS_COOKIE_NAME} cookie`,
        team: 'client-onboarding',
        context: {
          error: error instanceof z.ZodError ? error.format() : error,
        },
      });
    }
  }

  return undefined;
};

export const getResolvedDynamicProductAdData = ({
  dynamicProductAd,
  businessLine,
}: {
  dynamicProductAd?: Pick<DynamicProductAd, 'id' | 'grouping' | 'keyword'>;
  businessLine?: CapitalizedBusinessLine;
}) => {
  if (dynamicProductAd) return dynamicProductAd;

  const dynamicProductAdCookie = getDynamicAdsCookie();

  if (!dynamicProductAdCookie) return undefined;

  browserLogger({
    feature: 'dynamic-ads',
    level: 'info',
    message: `Found ${DYNAMIC_ADS_COOKIE_NAME} cookie but high-intent interstitial was not shown. Business line: ${
      businessLine || 'undefined'
    }`,
    team: 'client-onboarding',
    context: {
      cookieData: dynamicProductAdCookie,
      visitorId: getVisitorIdCookie(),
      referrer: window?.document?.referrer || '',
      businessLine,
    },
  });

  return dynamicProductAdCookie;
};
